import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import {
  useGetEntitySnapshot,
  useSearchChildEntities,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import _filter from 'lodash/filter';
import _toNumber from 'lodash/toNumber';
import { useEnvVariables } from '../../../../../../hooks/useEnvVariables';
import { useFetchInterfacePermissions } from '../../../../../access-control/hooks/useFetchInterfacePermissions';
import { useSavedInterfaceVersionDetails } from '../../../../../context/SavedInterfaceVersionDetailsProvider';
import InterfaceHelper from '../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { getLookupInterfaceQueryFilter } from '../../../../../utils/interfaceQueryFilters';
import type { UseFetchInterfaceAppResourcesHookType } from '../../../types';
import { useFetchSavedDataSourcesByComponentIdAndVersion } from '../useFetchSavedDataSourcesByComponentIdAndVersion';

const useFetchSavedInterfaceAppResourcesByVersion: UseFetchInterfaceAppResourcesHookType = ({
  interfaceId,
  mode,
  client,
}) => {
  const { currentVersion } = useSavedInterfaceVersionDetails();

  const {
    data: interfaceRecord,
    isLoading: isLoadingInterfaceRecord,
    error: errorInterfaceRecord,
  } = useGetEntitySnapshot<InterfaceEntity | undefined>(UAEntityTypes.Interface, interfaceId, {
    version: currentVersion,
  });

  const {
    permissions,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useFetchInterfacePermissions({
    interfaceId,
    client,
    mode,
  });

  const {
    envVariables,
    isLoading: envVariablesLoading,
    error: envVariablesError,
  } = useEnvVariables();

  const globalInterfacePageComponent = InterfaceHelper.getGlobalInterfacePageComponent(interfaceId);

  const {
    data: globalInterfacePageEntities,
    isLoading: isLoadingGlobalInterfacePage,
    error: errorGlobalInterfacePage,
  } = useSearchChildEntities<InterfacePageEntity[]>({
    entityType: UAEntityTypes.Component,
    parentEntityId: interfaceId,
    parentEntityType: UAEntityTypes.Interface,
    parentVersion: _toNumber(currentVersion),
    filter: getLookupInterfaceQueryFilter({
      interfaceId,
      interfaceComponent: globalInterfacePageComponent,
    }).filter,
  });

  const globalInterfacePage = _filter(globalInterfacePageEntities, {
    id: globalInterfacePageComponent.id,
  })[0] as InterfacePageEntity | undefined;

  const { dataSources, errorDS, isLoadingDS } = useFetchSavedDataSourcesByComponentIdAndVersion({
    interfaceComponentId: globalInterfacePageComponent.id,
    interfaceComponentVersion: globalInterfacePage?.version
      ? _toNumber(globalInterfacePage.version)
      : undefined,
  });

  const _isLoading =
    isLoadingInterfaceRecord ||
    permissionsLoading ||
    envVariablesLoading ||
    isLoadingDS ||
    isLoadingGlobalInterfacePage;

  const _error =
    errorInterfaceRecord ||
    permissionsError ||
    envVariablesError ||
    errorDS ||
    errorGlobalInterfacePage;

  return {
    isLoading: _isLoading,
    error: _error,
    interfaceRecord,
    permissions: permissions ?? {},
    envVariables,
    dataSources,
    globalInterfacePage,
  };
};

export default useFetchSavedInterfaceAppResourcesByVersion;
